// Canonical card composite-id module (Phase 5, Round 3 wave B).
//
// The prototype's card space is `${rarity}-card-${legacyNum}` — 5 rarities ×
// 306 catalog definitions. The server now stores ownership as
// (definition_id, rarity); this module is the single source of truth for
// translating between that composite id and the (rarity, legacyNum) pair both
// the client bridge and the import path build on.
//
// Pure, dependency-free TypeScript — no imports beyond the language itself, and
// no knowledge of the catalog size (306 is data, not code; bounds against the
// catalog happen at the callers).

export const CARD_RARITIES = ["common", "silver", "gold", "platinum", "ultimate"] as const;

export type CardRarity = (typeof CARD_RARITIES)[number];

const RARITY_SET: ReadonlySet<string> = new Set(CARD_RARITIES);

export interface CompositeIdInput {
  rarity: string;
  legacyNum: number;
}

export interface CompositeId {
  rarity: CardRarity;
  legacyNum: number;
}

/**
 * Format a (rarity, legacyNum) pair into the canonical composite id
 * `<rarity>-card-<legacyNum>`. Throws on unknown rarity or a legacyNum that is
 * not a positive integer — callers validate upstream, so this fails loud.
 */
export function formatCompositeId(input: CompositeIdInput): string {
  const { rarity, legacyNum } = input;
  if (!RARITY_SET.has(rarity)) {
    throw new Error(`formatCompositeId: unknown rarity "${rarity}"`);
  }
  if (
    typeof legacyNum !== "number" ||
    !Number.isInteger(legacyNum) ||
    legacyNum < 1
  ) {
    throw new Error(
      `formatCompositeId: legacyNum must be a positive integer (got ${String(legacyNum)})`,
    );
  }
  return `${rarity}-card-${legacyNum}`;
}

/**
 * Parse a composite id back into its (rarity, legacyNum) pair. Returns null for
 * anything malformed — unknown rarity, missing/extra segments, non-integer or
 * < 1 num, or an empty string. NEVER throws.
 */
export function parseCompositeId(id: string): CompositeId | null {
  try {
    if (typeof id !== "string" || id.length === 0) {
      return null;
    }
    const parts = id.split("-");
    if (parts.length !== 3) {
      return null;
    }
    const [rarity, card, numStr] = parts;
    if (card !== "card") {
      return null;
    }
    if (!RARITY_SET.has(rarity)) {
      return null;
    }
    // Digits only — rejects "1.5", "-3", "1e2", "", and any signed form.
    if (!/^\d+$/.test(numStr)) {
      return null;
    }
    const legacyNum = Number(numStr);
    if (!Number.isInteger(legacyNum) || legacyNum < 1) {
      return null;
    }
    return { rarity: rarity as CardRarity, legacyNum };
  } catch {
    return null;
  }
}